import { FC, MouseEvent, useCallback, useEffect, useMemo, useRef } from 'react';
import { createUseStyles } from 'react-jss';
import { FormProps } from '../helpers';
import ListToggle from '../list-toggle';
import { UiOption } from '@/lib/helpers';
import { Box, Stack } from '@mui/material';
import { PublicServiceMeta, PublicServiceModel } from '@/lib/models/public-service/public-service.model';
import useQueryHelper from '@/hooks/use-query-helper';
import useAppData from '@/hooks/use-app-data.hook';
import { AppDataQueryKey } from '@/lib/query-client';
import { PolicyModel } from '@/lib/models/policy-model/policy.model';
import Icon from '@/components/icon';
import {
  getPublicServiceOptions,
  PublicServiceConfig,
  PublicServiceOptionMeta,
} from '@/lib/models/policy-model/public-services';
import useDrawer from '@/hooks/use-drawer.hook';
import { buildServiceAccess } from './helpers';
import ServiceLabel from './service-label';

interface Props extends FormProps {
  value: PublicServiceConfig;
  onChange: (value: PublicServiceConfig) => void;
  aclVariant?: 'allow' | 'block';
}

const useStyles = createUseStyles({
  psList: {
    '& label.MuiFormControlLabel-root': {
      alignItems: 'flex-start',
      marginRight: 0,
    },
    '& span.MuiFormControlLabel-label': {
      display: 'block',
      width: '100%',
    },
    '& .MuiTypography-root': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      display: 'block',
      flexGrow: 1,
    },
  },
});

const PublicServiceAcl: FC<Props> = ({ value, onChange, aclVariant = 'allow', ...props }) => {
  const styles = useStyles();
  const isAllowList = aclVariant === 'allow';

  const { openDrawer } = useDrawer();

  const handleView = useCallback(
    (_event: MouseEvent, { id }: PublicServiceModel) => {
      openDrawer('public-service', { id });
    },
    [openDrawer]
  );

  const query = useAppData<PublicServiceMeta[]>(AppDataQueryKey.PublicServices);
  const { data = [] } = query;
  const { showLoader } = useQueryHelper(query);

  const valueRef = useRef(value);

  const [allowIds, blockIds, monitorIds] = useMemo(
    () => value?.acls || [new Set(), new Set(), new Set()],
    [value]
  );

  const onListToggleChange = useCallback(
    (option: UiOption[]) => {
      const selectedIds = option.map(({ value: optValue }) => optValue);
      const serviceAccess = buildServiceAccess(valueRef.current, selectedIds, aclVariant);
      onChange(new PublicServiceConfig({ ...valueRef.current, serviceAccess }));
    },
    [onChange, aclVariant]
  );

  const onSwitchChange = useCallback(
    (serviceId: string) => {
      if (!valueRef.current) {
        valueRef.current = new PublicServiceConfig({ serviceAccess: [] });
      }

      const entityIndex = valueRef.current.serviceAccess?.findIndex(
        ({ serviceId: id }) => id === serviceId
      );
      const entity =
        entityIndex && entityIndex > -1 ? valueRef.current.serviceAccess?.[entityIndex] : null;

      if (!entity) {
        valueRef.current.serviceAccess!.push({ serviceId, action: 'monitored' });
      } else {
        entity.action = entity.action === 'monitored' ? 'protected' : 'monitored';
        valueRef.current.serviceAccess![entityIndex!] = entity;
      }

      onChange(valueRef.current);
    },
    [onChange]
  );

  const psOptions: UiOption[] = useMemo(() => {
    const policy = new PolicyModel({ publicServices: value });
    const { newPublicServices } = policy;

    return getPublicServiceOptions(data, aclVariant, policy).map(({ value: optValue, meta }) => {
      const { publicService, disabledByAlternateList } = meta as PublicServiceOptionMeta;
      const { id, name } = publicService;

      const isNewService = newPublicServices.includes(id);
      const disabled = disabledByAlternateList || isNewService;

      const LabelEl = (
        <ServiceLabel
          isAllowList={isAllowList}
          isNewService={isNewService}
          disabled={disabled}
          publicService={publicService}
          handleView={handleView}
          onSwitchChange={onSwitchChange}
          isMonitor={monitorIds.has(id)}
          isAllow={allowIds.has(id)}
        />
      );

      return {
        label: LabelEl,
        value: optValue,
        meta: { keyword: name },
        disabled,
      };
    });
  }, [data, handleView, onSwitchChange, isAllowList, aclVariant, value, allowIds, monitorIds]);

  const rightTitle = (
    <Stack direction="row" alignItems="center" gap={1}>
      <Icon name={isAllowList ? 'check' : 'block'} size="small" />
      {isAllowList ? 'Allowed Services' : 'Blocked Services'}
    </Stack>
  );

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  return (
    <Box className={styles.psList}>
      <ListToggle
        {...props}
        value={isAllowList ? Array.from(allowIds) : Array.from(blockIds)}
        onChange={onListToggleChange}
        label="Services"
        leftTitle="Available Services"
        rightTitle={rightTitle}
        options={psOptions}
        labelChecks={true}
        loadState={showLoader ? 'loading' : 'loaded'}
      />
    </Box>
  );
};

export default PublicServiceAcl;
